import { useState } from 'react';
import { Navigate } from 'react-router';
import {
  Button,
  Card,
  Flex,
  Form,
  Input,
  message,
  Modal,
  Select,
  Typography,
} from 'antd';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { createAdmin } from '@/api/admin';
import { PageTitle } from '@/components/ui/PageTitle';
import { useAuthValidation } from '@/hooks/useAuthValidation';
import { UserRole } from '@/types';
import { ROUTES } from '@/types/routes';

const { Title, Text, Paragraph } = Typography;

type AdminFormValues = {
  firstName: string;
  lastName: string;
  email: string;
  role: UserRole.ADMIN | UserRole.SUPER_ADMIN;
};

// TCX-666: super-admin-only screen to create ADMIN / SUPER_ADMIN accounts. The
// BE (PR #572) returns a one-time temporaryPassword for these roles; we show it
// exactly once and then discard it (never recoverable / re-fetchable).
const AdminAdministrators = () => {
  const role = useAuthValidation();
  const [form] = Form.useForm<AdminFormValues>();
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [createdEmail, setCreatedEmail] = useState<string>('');

  const mutation = useMutation({
    mutationFn: createAdmin,
    onSuccess: (data, variables) => {
      form.resetFields();
      setCreatedEmail(variables.email);
      setTempPassword(data?.temporaryPassword ?? null);
      if (!data?.temporaryPassword) {
        // Defensive: a privileged-role create should always return one.
        message.success('Administrator created.');
      }
    },
    onError: (error: unknown) => {
      const fallback = 'Failed to create administrator. Please try again.';
      const msg = axios.isAxiosError(error)
        ? ((error.response?.data as { message?: string })?.message ?? fallback)
        : fallback;
      message.error(msg);
    },
  });

  // AdminDefaultLayout already admits superAdmin into the shell; this guards the
  // privileged screen itself. `role` is null while the JWT is still being read,
  // so only redirect once it resolves to a non-super role (mirrors the shell).
  if (role && role !== 'superAdmin') {
    return <Navigate to={ROUTES.ADMIN_DASHBOARD} replace />;
  }

  // Discard the one-time password — it is never recoverable.
  const handleCloseModal = () => {
    setTempPassword(null);
    setCreatedEmail('');
  };

  return (
    <Flex vertical gap={24}>
      <PageTitle title="Administrators" />
      <Card style={{ maxWidth: 560 }}>
        <Title level={5} style={{ marginTop: 0 }}>
          Create administrator
        </Title>
        <Text type="secondary">
          Creates an admin or super-admin. A one-time temporary password is shown
          once on success — copy it before closing.
        </Text>
        <Form
          form={form}
          layout="vertical"
          requiredMark
          style={{ marginTop: 24 }}
          initialValues={{ role: UserRole.ADMIN }}
          onFinish={(values) => mutation.mutate(values)}
        >
          <Form.Item
            name="firstName"
            label="First name"
            rules={[{ required: true, message: 'First name is required' }]}
          >
            <Input placeholder="First name" />
          </Form.Item>
          <Form.Item
            name="lastName"
            label="Last name"
            rules={[{ required: true, message: 'Last name is required' }]}
          >
            <Input placeholder="Last name" />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Email is required' },
              { type: 'email', message: 'Enter a valid email' },
            ]}
          >
            <Input placeholder="name@example.com" />
          </Form.Item>
          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: 'Role is required' }]}
          >
            <Select
              options={[
                { label: 'Admin', value: UserRole.ADMIN },
                { label: 'Super Admin', value: UserRole.SUPER_ADMIN },
              ]}
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={mutation.isPending}
            >
              Create administrator
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <Modal
        open={!!tempPassword}
        title="Administrator created"
        onCancel={handleCloseModal}
        maskClosable={false}
        footer={[
          <Button key="done" type="primary" onClick={handleCloseModal}>
            Done
          </Button>,
        ]}
      >
        <Paragraph>
          {createdEmail ? <Text strong>{createdEmail}</Text> : 'The account'} was
          created. Copy the temporary password now — it is shown only once and
          cannot be recovered.
        </Paragraph>
        <Paragraph
          copyable={{ text: tempPassword ?? '' }}
          style={{
            background: '#f5f5f5',
            padding: '8px 12px',
            borderRadius: 6,
            fontFamily: 'monospace',
            wordBreak: 'break-all',
          }}
        >
          {tempPassword}
        </Paragraph>
        <Text type="warning">
          Share it securely. The new administrator should change it after first
          sign-in.
        </Text>
      </Modal>
    </Flex>
  );
};

export default AdminAdministrators;
